<template>
  <div class="login">
    <div class="auth-container">
      <h1>{{ $t('auth.login') }}</h1>
      <p>{{ $t('auth.welcome_back') }}</p>
      
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <div v-if="apiStatus === 'error'" class="warning-message">
        {{ $t('auth.api_connection_error') }}
      </div>
      
      <form @submit.prevent="handleLogin" class="auth-form">
        <div class="form-group">
          <label for="username">{{ $t('auth.username') }}</label>
          <input 
            type="text" 
            id="username" 
            v-model="username" 
            required 
            :placeholder="$t('auth.username')"
            :disabled="isLoading"
          />
        </div>
        
        <div class="form-group">
          <label for="password">{{ $t('auth.password') }}</label>
          <input 
            type="password" 
            id="password" 
            v-model="password" 
            required 
            :placeholder="$t('auth.password')"
            :disabled="isLoading"
          />
        </div>
        
        <button type="submit" class="btn login-btn" :disabled="isLoading">
          <span v-if="isLoading">
            <div class="spinner-small"></div>
            {{ $t('common.loading') }}
          </span>
          <span v-else>{{ $t('auth.login') }}</span>
        </button>
      </form>
      
      <div class="auth-links">
        <p>{{ $t('auth.no_account') }} <router-link to="/register">{{ $t('auth.register_here') }}</router-link></p>
        <p><router-link to="/forgot-password">{{ $t('auth.forgot_password_link') }}</router-link></p> 
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/authStore';
import { logger } from '@/utils/logger';
import axios from 'axios';

const router = useRouter();
const authStore = useAuthStore();

const username = ref('');
const password = ref('');
const apiStatus = ref('unknown'); // 'unknown', 'ok', 'error'

const isLoading = computed(() => authStore.isLoading);
const error = computed(() => authStore.getError);

// Test API connection
const checkAPIConnection = async () => {
  try {
    // Use the correct health endpoint path
    await axios.get('/health', { 
      timeout: 3000,
      withCredentials: false
    });
    apiStatus.value = 'ok';
    logger.debug('API connection test successful');
  } catch (err) {
    // Check for 404 error - this could mean the health endpoint isn't implemented
    // If it's a 404, we'll assume the API is still available
    if (err.response && err.response.status === 404) {
      // 404 might just mean the health endpoint isn't implemented
      logger.debug('Health endpoint not found, but API seems reachable');
      apiStatus.value = 'ok';
    } else {
      console.error('API connection test failed:', err);
      apiStatus.value = 'error';
    }
  }
};

// Clear error when component mounts or unmounts
onMounted(async () => {
  authStore.clearError();
  logger.trackEvent('login_view_opened');
  
  // Try to check API connection, but don't block login if it fails
  try {
    await checkAPIConnection();
  } catch (err) {
    logger.error('API check failed but continuing anyway:', err);
  }
  
  // If there's a loading state stuck from a previous session, reset it
  if (authStore.isLoading) {
    authStore.loading = false;
  }
});

onBeforeUnmount(() => {
  authStore.clearError();
});

const handleLogin = async () => {
  try {
    // Clear any previous errors
    authStore.clearError();
    
    // Even if API check failed, we'll attempt login anyway
    // since the health endpoint might not exist
    
    logger.trackEvent('login_attempt', { username: username.value });
    
    // Set a local timeout to handle potential hung requests
    const loginTimeout = setTimeout(() => {
      if (authStore.isLoading) {
        authStore.loading = false;
        authStore.error = 'Login request timed out. Please try again.';
      }
    }, 15000); // 15-second timeout
    
    await authStore.login({
      username: username.value,
      password: password.value
    });
    
    clearTimeout(loginTimeout);
    
    if (!error.value) {
      logger.trackEvent('login_success', { username: username.value });
      router.push('/my-plants');
    } else {
      logger.trackEvent('login_failed', { 
        username: username.value,
        error: error.value 
      });
    }
  } catch (err) {
    logger.error('Login error:', err, { username: username.value });
    
    // Log specific error details
    if (err.response) {
      logger.error('Response error:', {
        status: err.response.status,
        statusText: err.response.statusText
      });
    }
    
    // Ensure loading state is reset
    if (authStore.isLoading) {
      authStore.loading = false;
    }
    
    // Set a user-friendly error message
    authStore.error = 'Login failed. Please check your credentials and try again.';
  }
};
</script>

<style scoped>
.login {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
}

.auth-container {
  background-color: white;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 2rem;
  width: 100%;
  max-width: 480px;
  border-top: 4px solid var(--primary-color);
}

.auth-container h1 {
  margin-bottom: 0.5rem;
  color: var(--primary-color);
}

.auth-container p {
  margin-bottom: 1.5rem;
  color: var(--text-light);
}

.auth-form {
  margin-top: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  font-size: 1rem;
  transition: border-color 0.3s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.login-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  height: 44px;
}

.login-btn span {
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn:disabled {
  background-color: #9e9e9e;
  cursor: not-allowed;
}

.auth-links {
  margin-top: 1.5rem;
  text-align: center;
}

.auth-links a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
}

.auth-links a:hover {
  text-decoration: underline;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: var(--radius);
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--error-color);
}

.warning-message {
  background-color: #fffde7;
  color: #f57f17;
  padding: 1rem;
  border-radius: var(--radius);
  margin-bottom: 1.5rem;
  border-left: 4px solid #fbc02d;
}

.spinner-small {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .auth-container {
    padding: 1.5rem;
    max-width: 90%;
    margin: 0 auto;
  }
}
</style>
